/**
 * ResultsTable Component
 * Display search results using TanStack Table
 */

import React from 'react';
import type { Professional } from './types/api.types';
import Button from './ui/Button';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

interface ResultsTableProps {
  results: Professional[];
  isLoading: boolean;
  onExportCsv?: () => void;
  canExport?: boolean;
  tier?: string;
}

const columnHelper = createColumnHelper<Professional>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: info => (
      <div>
        <div className="text-sm font-medium text-slate-200">{info.getValue()}</div>
        {info.row.original.email && (
          <div className="text-sm text-slate-400">{info.row.original.email}</div>
        )}
      </div>
    ),
  }),
  columnHelper.accessor('license_number', {
    header: 'License #',
    cell: info => <div className="text-sm text-slate-300 font-mono">{info.getValue()}</div>,
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => {
      const status = info.getValue()?.toLowerCase() || 'unknown';
      const isActive = status === 'active' || status === 'current';
      return (
        <div className="flex items-center space-x-2">
          <span className={`text-${isActive ? 'green' : 'yellow'}-500`}>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              {isActive ? (
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              )}
            </svg>
          </span>
          <span className="text-sm text-slate-300 capitalize">{status}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('city', {
    header: 'Location',
    cell: info => {
      const city = info.getValue();
      const state = info.row.original.state;
      return (
        <div className="text-sm text-slate-300">
          {city && state ? `${city}, ${state}` : state || 'N/A'}
        </div>
      );
    },
  }),
  columnHelper.accessor('brokerage', {
    header: 'Brokerage',
    cell: info => <div className="text-sm text-slate-300">{info.getValue() || 'N/A'}</div>,
  }),
];

const ResultsTable: React.FC<ResultsTableProps> = ({
  results,
  isLoading,
  onExportCsv,
  canExport = false,
}) => {
  const table = useReactTable({
    data: results,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleUpgradeClick = () => {
    window.location.href = '/?page=pricing';
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-12">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="animate-spin h-12 w-12 text-sky-500 rounded-full border-4 border-t-transparent border-current" />
          <p className="text-slate-400 text-lg">Loading results...</p>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-12">
        <div className="text-center text-slate-400">
          <h3 className="text-xl font-bold text-slate-300 mb-2">No results found</h3>
          <p>Try adjusting your search criteria</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
        <div>
          <h3 className="text-lg font-bold text-slate-200">Search Results</h3>
          <p className="text-sm text-slate-400 mt-1">{results.length} professionals found</p>
        </div>
        <div>
          {canExport ? (
            <Button variant="primary" size="sm" onClick={onExportCsv}>
              Export CSV
            </Button>
          ) : (
            <div className="relative group">
              <Button variant="secondary" size="sm" disabled>Export CSV</Button>
              <div className="absolute top-full right-0 mt-2 w-64 bg-slate-900 border border-slate-700 rounded-lg p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <p className="text-sm text-slate-300 mb-3">CSV export is available on paid plans</p>
                <Button variant="primary" size="sm" className="w-full" onClick={handleUpgradeClick}>Upgrade to Starter</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-900">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id} className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-slate-700">
            {table.getRowModel().rows.map(row => (
              <tr key={row.id} className="hover:bg-slate-750 transition-colors">
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResultsTable;
